/**
 * WebAuthn/Passkey utilities for u.software core
 *
 * Provides helper functions for:
 * - Base64URL encoding/decoding (required for WebAuthn)
 * - Platform authenticator availability detection
 */

/**
 * Converts a Base64URL string to an ArrayBuffer
 * WebAuthn uses Base64URL encoding which differs from standard Base64
 * - Uses '-' instead of '+'
 * - Uses '_' instead of '/'
 * - No padding '='
 */
export function base64ToBuffer(base64: string): ArrayBuffer {
  // Convert Base64URL to standard Base64
  const standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/')
  // Add padding if needed
  const padded = standardBase64.padEnd(
    standardBase64.length + ((4 - (standardBase64.length % 4)) % 4),
    '='
  )
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Converts an ArrayBuffer to a Base64URL string
 * WebAuthn responses need to be encoded as Base64URL for transmission
 */
export function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  // Convert to Base64URL (no padding, URL-safe characters)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/**
 * Checks if passkey/WebAuthn is available on this device
 * Returns true if:
 * - The browser supports WebAuthn (PublicKeyCredential API)
 * - A platform authenticator is available (Face ID, Touch ID, Windows Hello, etc.)
 */
export async function isPasskeyAvailable(): Promise<boolean> {
  // Check if WebAuthn API is available
  if (!window.PublicKeyCredential) {
    return false
  }

  try {
    // Check if a platform authenticator (biometrics, etc.) is available
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

/**
 * Checks if conditional UI (autofill) is supported for passkeys
 * This allows passkeys to appear in the autofill dropdown
 */
export async function isConditionalUIAvailable(): Promise<boolean> {
  if (!window.PublicKeyCredential) {
    return false
  }

  try {
    // Check if conditional mediation is supported
    if (
      typeof PublicKeyCredential.isConditionalMediationAvailable === 'function'
    ) {
      return await PublicKeyCredential.isConditionalMediationAvailable()
    }
    return false
  } catch {
    return false
  }
}

/**
 * Prepares PublicKeyCredentialCreationOptions from server response
 * Converts Base64URL strings to ArrayBuffers as required by WebAuthn
 */
export function prepareCreationOptions(
  options: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: base64ToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64ToBuffer(options.user.id),
    },
    excludeCredentials: options.excludeCredentials?.map((cred) => ({
      ...cred,
      id: base64ToBuffer(cred.id),
    })),
  }
}

/**
 * Prepares PublicKeyCredentialRequestOptions from server response
 * Converts Base64URL strings to ArrayBuffers as required by WebAuthn
 */
export function prepareRequestOptions(
  options: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: base64ToBuffer(options.challenge),
    allowCredentials: options.allowCredentials?.map((cred) => ({
      ...cred,
      id: base64ToBuffer(cred.id),
    })),
  }
}

/**
 * Encodes a PublicKeyCredential (registration response) for transmission to server
 */
export function encodeRegistrationResponse(
  credential: PublicKeyCredential
): EncodedRegistrationResponse {
  const response = credential.response as AuthenticatorAttestationResponse

  return {
    id: credential.id,
    rawId: bufferToBase64(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64(response.clientDataJSON),
      attestationObject: bufferToBase64(response.attestationObject),
      transports: response.getTransports?.() || [],
    },
  }
}

/**
 * Encodes a PublicKeyCredential (authentication response) for transmission to server
 */
export function encodeAuthenticationResponse(
  credential: PublicKeyCredential
): EncodedAuthenticationResponse {
  const response = credential.response as AuthenticatorAssertionResponse

  return {
    id: credential.id,
    rawId: bufferToBase64(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64(response.clientDataJSON),
      authenticatorData: bufferToBase64(response.authenticatorData),
      signature: bufferToBase64(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64(response.userHandle)
        : null,
    },
  }
}

// Type definitions for JSON-serialized WebAuthn options from server

export interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string
  rp: {
    name: string
    id?: string
  }
  user: {
    id: string
    name: string
    displayName: string
  }
  pubKeyCredParams: Array<{
    type: 'public-key'
    alg: number
  }>
  timeout?: number
  excludeCredentials?: Array<{
    type: 'public-key'
    id: string
    transports?: AuthenticatorTransport[]
  }>
  authenticatorSelection?: AuthenticatorSelectionCriteria
  attestation?: AttestationConveyancePreference
}

export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string
  timeout?: number
  rpId?: string
  allowCredentials?: Array<{
    type: 'public-key'
    id: string
    transports?: AuthenticatorTransport[]
  }>
  userVerification?: UserVerificationRequirement
}

export interface EncodedRegistrationResponse {
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    attestationObject: string
    transports: string[]
  }
}

export interface EncodedAuthenticationResponse {
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
    userHandle: string | null
  }
}
